import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, Sparkles, ChevronLeft, ChevronRight, Rocket, Mail, MessageSquare, Phone, Check, Pencil, RotateCcw, Plus, Tag } from 'lucide-react';
import apiV2 from '../../utils/apiV2';
import { useOperatorLocale } from '../../hooks/useOperatorLocale';
import { toast } from 'sonner';

const GOALS = [
  { id: 'win_back', title: 'Win back lapsed customers', desc: 'Re-engage past guests who haven\'t booked in a while.' },
  { id: 'promote', title: 'Promote a service', desc: 'Put your best experiences in front of past customers.' },
  { id: 'announce', title: 'Announce an offer', desc: 'Share a discount or special deal with your audience.' },
];

const CHANNEL_META = {
  email: { label: 'Email', icon: Mail },
  sms: { label: 'SMS', icon: MessageSquare },
  whatsapp: { label: 'WhatsApp', icon: Phone },
};

const BUDGETS = [100, 300, 500, 1000];

const Field = ({ label, children }) => (
  <div className="mb-4">
    <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls = 'w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB]';

const VariantCard = ({ v, i, channels, onPatch, onToggleKept, onToggleEdit }) => (
  <div
    data-testid={`wizard-variant-card-${i}`}
    className={`rounded-xl border p-4 transition-opacity ${v.kept ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-55'}`}
  >
    <div className="flex items-center justify-between gap-2 mb-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="px-2 py-0.5 rounded-full bg-slate-900 text-white text-[10px] font-extrabold shrink-0">Ad {i + 1}</span>
        {v.angle && <span className="px-2 py-0.5 rounded-full bg-[#EBF2FF] text-[#2563EB] text-[10px] font-bold truncate">{v.angle}</span>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {v.kept ? (
          <>
            <button onClick={() => onToggleEdit(v.key)} title={v.editing ? 'Done editing' : 'Edit this ad'} data-testid={`wizard-variant-edit-${i}`}
              className={`p-1.5 rounded-full text-xs ${v.editing ? 'bg-[#2563EB] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
              {v.editing ? <Check className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => onToggleKept(v.key)} title="Discard this ad" data-testid={`wizard-variant-discard-${i}`}
              className="p-1.5 rounded-full bg-slate-100 text-slate-400 hover:bg-red-50 hover:text-red-500">
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <button onClick={() => onToggleKept(v.key)} data-testid={`wizard-variant-restore-${i}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold hover:bg-slate-300">
            <RotateCcw className="w-3 h-3" /> Restore
          </button>
        )}
      </div>
    </div>

    {v.editing && v.kept ? (
      <div className="space-y-3">
        {channels.includes('email') && (
          <>
            <input value={v.emailSubject} onChange={(e) => onPatch(v.key, { emailSubject: e.target.value })}
              placeholder="Email subject" data-testid={`wizard-variant-subject-${i}`} className={inputCls} />
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={v.emailHeadline} onChange={(e) => onPatch(v.key, { emailHeadline: e.target.value })}
                placeholder="Masthead headline" className={inputCls} />
              <input value={v.ctaLabel} onChange={(e) => onPatch(v.key, { ctaLabel: e.target.value })}
                placeholder="Button label (Book now)" className={inputCls} />
            </div>
            <textarea value={v.emailBody} onChange={(e) => onPatch(v.key, { emailBody: e.target.value })}
              rows={4} placeholder="Email body" data-testid={`wizard-variant-body-${i}`} className={inputCls} />
          </>
        )}
        {channels.includes('sms') && (
          <textarea value={v.smsBody} onChange={(e) => onPatch(v.key, { smsBody: e.target.value })}
            rows={2} placeholder={`SMS message (${(v.smsBody || '').length} chars)`} data-testid={`wizard-variant-sms-${i}`} className={inputCls} />
        )}
        {channels.includes('whatsapp') && (
          <textarea value={v.whatsappBody} onChange={(e) => onPatch(v.key, { whatsappBody: e.target.value })}
            rows={2} placeholder="WhatsApp message" data-testid={`wizard-variant-whatsapp-${i}`} className={inputCls} />
        )}
      </div>
    ) : (
      <div className="space-y-1.5">
        {channels.includes('email') && (
          <>
            <div className="text-sm font-bold text-slate-800 truncate">{v.emailSubject || <span className="text-slate-300">No email subject yet</span>}</div>
            <p className="text-xs text-slate-500 leading-relaxed" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {v.emailBody || 'No email body yet'}
            </p>
          </>
        )}
        {channels.includes('sms') && v.smsBody && (
          <div className="flex items-start gap-1.5 text-[11px] text-slate-500">
            <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-[#2563EB]" />
            <span className="truncate">{v.smsBody}</span>
          </div>
        )}
        {channels.includes('whatsapp') && v.whatsappBody && (
          <div className="flex items-start gap-1.5 text-[11px] text-slate-500">
            <Phone className="w-3 h-3 mt-0.5 shrink-0 text-[#2563EB]" />
            <span className="truncate">{v.whatsappBody}</span>
          </div>
        )}
      </div>
    )}
  </div>
);

export const CampaignWizard = ({ open, onClose, onCreated, overview, initial }) => {
  const orgId = localStorage.getItem('organizationId');
  const { formatMoney } = useOperatorLocale();
  const [step, setStep] = useState(0);
  const [segments, setSegments] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [destChoice, setDestChoice] = useState('website');
  const [variants, setVariants] = useState([]);
  const keyRef = useRef(0);
  const [form, setForm] = useState({
    goal: 'win_back', details: '', segment: 'opted_in', channels: [],
    dailyBudgetCents: 300, frequencyDays: 7, destinationUrl: '', heroImageUrl: '',
    discountCode: '', aiGenerated: false, name: '',
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setDestChoice('website');
    setVariants([]);
    set({
      channels: overview?.allowedChannels || [],
      destinationUrl: overview?.defaultDestination || '',
      heroImageUrl: '', discountCode: '',
      goal: initial?.goal && GOALS.some((g) => g.id === initial.goal) ? initial.goal : 'win_back',
      segment: initial?.segment || 'opted_in',
    });
    apiV2.get(`/organizations/${orgId}/intelligence/audiences?months=12`)
      .then((res) => setSegments(res.data.segments || []))
      .catch(() => setSegments([]));
  }, [open]); // eslint-disable-line

  if (!open) return null;
  const rates = overview?.rates || { email: 2, sms: 10, whatsapp: 25 };
  const allowed = overview?.allowedChannels || ['email'];
  const pendingChannels = overview?.pendingChannels || [];
  const destinations = overview?.destinations || [];
  const imageOptions = overview?.imageOptions || [];
  const promoCodes = overview?.promoCodes || [];
  const seg = segments.find((s) => s.id === form.segment);
  const segName = seg?.name || form.segment;
  const segCount = seg?.count ?? null;
  const cheapest = form.channels.length ? Math.min(...form.channels.map((c) => rates[c])) : rates.email;
  const priciest = form.channels.length ? Math.max(...form.channels.map((c) => rates[c])) : rates.email;
  const rawLo = Math.floor(form.dailyBudgetCents / priciest);
  const rawHi = Math.floor(form.dailyBudgetCents / cheapest);
  const reachLo = segCount != null ? Math.min(rawLo, segCount) : rawLo;
  const reachHi = segCount != null ? Math.min(rawHi, segCount) : rawHi;
  const coversAll = segCount != null && segCount > 0 && rawLo >= segCount;
  const reachText = coversAll
    ? `all ${segCount.toLocaleString()} contacts in this segment each day`
    : `up to ${reachLo === reachHi ? reachLo : `${reachLo}–${reachHi}`}${segCount != null ? ` of your ${segCount.toLocaleString()}` : ''} contacts/day`;
  const kept = variants.filter((v) => v.kept);

  const variantComplete = (v) => {
    if (form.channels.includes('email') && (!v.emailSubject || !v.emailBody)) return false;
    if (form.channels.includes('sms') && !v.smsBody) return false;
    if (form.channels.includes('whatsapp') && !v.whatsappBody) return false;
    return true;
  };

  const patchVariant = (key, patch) => setVariants((vs) => vs.map((v) => (v.key === key ? { ...v, ...patch } : v)));
  const toggleKept = (key) => setVariants((vs) => vs.map((v) => (v.key === key ? { ...v, kept: !v.kept } : v)));
  const toggleEdit = (key) => setVariants((vs) => vs.map((v) => (v.key === key ? { ...v, editing: !v.editing } : v)));

  const draft = async () => {
    setDrafting(true);
    try {
      const promo = promoCodes.find((p) => p.code === form.discountCode);
      const res = await apiV2.post(`/organizations/${orgId}/marketing/campaigns/generate`, {
        goal: form.goal, details: form.details || null, segment_name: segName, channels: form.channels,
        count: 5,
        discount_code: form.discountCode || null,
        discount_label: promo?.label || null,
        avoid_subjects: kept.map((v) => v.emailSubject).filter(Boolean),
      });
      const fresh = (res.data.variants || []).map((rv) => ({
        key: ++keyRef.current, angle: rv.angle || '',
        emailSubject: rv.email_subject || '', emailHeadline: rv.email_headline || '',
        ctaLabel: rv.cta_label || '', emailBody: rv.email_body || '',
        smsBody: rv.sms_body || '', whatsappBody: rv.whatsapp_body || '',
        kept: true, editing: false,
      }));
      setVariants((vs) => [...vs, ...fresh]);
      set({ aiGenerated: !!res.data.aiGenerated });
      toast.success(`${fresh.length} ad variation${fresh.length === 1 ? '' : 's'} drafted — discard any you don't like`);
    } catch {
      toast.error('Drafting failed — try again');
    } finally {
      setDrafting(false);
    }
  };

  const addManual = () => {
    setVariants((vs) => [...vs, {
      key: ++keyRef.current, angle: 'Your own', emailSubject: '', emailHeadline: '',
      ctaLabel: '', emailBody: '', smsBody: '', whatsappBody: '', kept: true, editing: true,
    }]);
  };

  const canNext = () => {
    if (step === 1) return !!form.segment;
    if (step === 2) return form.channels.length > 0 && form.dailyBudgetCents >= cheapest;
    if (step === 3) return kept.length > 0 && kept.every(variantComplete);
    return true;
  };

  const save = async (launch) => {
    setSaving(true);
    try {
      const base = kept[0];
      const payload = {
        name: form.name || `${GOALS.find((g) => g.id === form.goal)?.title} · ${segName}`,
        goal: form.goal, details: form.details || null, segment: form.segment, months: 12,
        channels: form.channels, daily_budget_cents: form.dailyBudgetCents,
        frequency_days: form.frequencyDays, destination_url: form.destinationUrl || null,
        hero_image_url: form.heroImageUrl || null,
        discount_code: form.discountCode || null,
        email_subject: base?.emailSubject || null, email_body: base?.emailBody || null,
        email_headline: base?.emailHeadline || null, cta_label: base?.ctaLabel || null,
        sms_body: base?.smsBody || null, whatsapp_body: base?.whatsappBody || null,
        ai_generated: form.aiGenerated,
        variants: kept.map((v) => ({
          angle: v.angle || null,
          email_subject: v.emailSubject || null, email_headline: v.emailHeadline || null,
          email_body: v.emailBody || null, cta_label: v.ctaLabel || null,
          sms_body: v.smsBody || null, whatsapp_body: v.whatsappBody || null,
        })),
      };
      const res = await apiV2.post(`/organizations/${orgId}/marketing/campaigns`, payload);
      if (launch) {
        await apiV2.post(`/organizations/${orgId}/marketing/campaigns/${res.data.id}/launch`);
        toast.success('Campaign launched — it will drip daily within your budget');
      } else {
        toast.success('Campaign saved as draft');
      }
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save campaign');
    } finally {
      setSaving(false);
    }
  };

  const steps = ['Goal', 'Audience', 'Budget', 'Ads', 'Review'];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 overflow-y-auto" data-testid="campaign-wizard">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-6 shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100">
          <div>
            <div className="font-extrabold text-slate-900">New campaign</div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">
              Step {step + 1} of 5 · {steps[step]}
            </div>
          </div>
          <button onClick={onClose} data-testid="wizard-close-btn" className="p-2 rounded-full hover:bg-slate-100 text-slate-400"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 max-h-[62vh] overflow-y-auto">
          {step === 0 && (
            <>
              <div className="grid gap-3 mb-4">
                {GOALS.map((g) => (
                  <button key={g.id} onClick={() => set({ goal: g.id })} data-testid={`wizard-goal-${g.id}`}
                    className={`text-left p-4 rounded-xl border transition-colors ${form.goal === g.id ? 'border-[#2563EB] bg-[#EBF2FF]' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="font-bold text-sm text-slate-900">{g.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{g.desc}</div>
                  </button>
                ))}
              </div>
              <Field label="Anything specific? (optional)">
                <textarea value={form.details} onChange={(e) => set({ details: e.target.value })} rows={2}
                  data-testid="wizard-details-input" placeholder="e.g. 20% off sunset cruises in July"
                  className={inputCls} />
              </Field>
            </>
          )}

          {step === 1 && (
            <>
              <p className="text-xs text-slate-500 mb-4">Only contacts who <span className="font-bold">opted in to marketing</span> will ever be messaged, regardless of segment.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {segments.map((s) => (
                  <button key={s.id} onClick={() => set({ segment: s.id })} data-testid={`wizard-segment-${s.id}`}
                    className={`text-left p-4 rounded-xl border transition-colors ${form.segment === s.id ? 'border-[#2563EB] bg-[#EBF2FF]' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-sm text-slate-900">{s.name}</span>
                      <span className="text-sm font-extrabold text-[#2563EB]">{s.count.toLocaleString()}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 leading-snug">{s.desc}</div>
                  </button>
                ))}
                {segments.length === 0 && <p className="text-sm text-slate-400 col-span-2">Loading segments…</p>}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Field label="Channels">
                <div className="flex gap-2 flex-wrap">
                  {[...allowed, ...pendingChannels].map((ch) => {
                    const Icon = CHANNEL_META[ch].icon;
                    const isPending = pendingChannels.includes(ch);
                    const on = !isPending && form.channels.includes(ch);
                    return (
                      <button key={ch} data-testid={`wizard-channel-${ch}`} disabled={isPending}
                        title={isPending ? `${CHANNEL_META[ch].label} campaigns are coming soon` : undefined}
                        onClick={() => !isPending && set({ channels: on ? form.channels.filter((c) => c !== ch) : [...form.channels, ch] })}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-bold transition-colors ${isPending ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed' : on ? 'border-[#2563EB] bg-[#EBF2FF] text-[#2563EB]' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                        <Icon className="w-4 h-4" /> {CHANNEL_META[ch].label}
                        {isPending
                          ? <span className="text-[9px] font-extrabold uppercase tracking-wider bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">Soon</span>
                          : <span className="text-[10px] font-extrabold opacity-70">{formatMoney(rates[ch] / 100)}/msg</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">Channels available are based on your country's marketing rules and which dedicated marketing senders are configured — SMS/WhatsApp never send through your booking-notification number.</p>
              </Field>
              <Field label="Daily budget">
                <div className="flex gap-2 flex-wrap items-center">
                  {BUDGETS.map((b) => (
                    <button key={b} onClick={() => set({ dailyBudgetCents: b })} data-testid={`wizard-budget-${b}`}
                      className={`px-4 py-2 rounded-full border text-sm font-bold ${form.dailyBudgetCents === b ? 'border-[#2563EB] bg-[#EBF2FF] text-[#2563EB]' : 'border-slate-200 text-slate-500'}`}>
                      {formatMoney(b / 100)}/day
                    </button>
                  ))}
                  <input type="number" min="0.1" step="0.5" value={(form.dailyBudgetCents / 100).toString()}
                    data-testid="wizard-budget-custom"
                    onChange={(e) => set({ dailyBudgetCents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                    className="w-24 px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                </div>
                <p className="text-[11px] text-slate-500 mt-2" data-testid="wizard-reach-estimate">
                  ≈ <span className="font-bold">{reachText}</span> · you only pay per message sent, unused budget never rolls over
                </p>
                {coversAll && (
                  <p className="text-[11px] text-slate-400 mt-1" data-testid="wizard-reach-cap-note">
                    This budget already covers your whole segment — we'll never send beyond your {segCount.toLocaleString()} contacts (and the frequency cap below still applies).
                  </p>
                )}
              </Field>
              <Field label="Contact each customer at most">
                <select value={form.frequencyDays} onChange={(e) => set({ frequencyDays: Number(e.target.value) })}
                  data-testid="wizard-frequency-select" className={inputCls}>
                  <option value={7}>Once a week</option>
                  <option value={14}>Every 2 weeks</option>
                  <option value={30}>Once a month</option>
                </select>
              </Field>
              <Field label="Where the link goes">
                <select
                  value={destChoice}
                  data-testid="wizard-destination-select"
                  onChange={(e) => {
                    const v = e.target.value;
                    setDestChoice(v);
                    if (v === 'custom') set({ destinationUrl: '' });
                    else {
                      const d = destinations.find((x) => x.id === v);
                      if (d) set({ destinationUrl: d.url });
                    }
                  }}
                  className={inputCls}>
                  {destinations.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  <option value="custom">Custom URL…</option>
                </select>
                {destChoice === 'custom' ? (
                  <input value={form.destinationUrl} onChange={(e) => set({ destinationUrl: e.target.value })}
                    placeholder="https://…" data-testid="wizard-destination-input" className={`${inputCls} mt-2`} />
                ) : (
                  <p className="text-[11px] text-slate-400 mt-1.5 truncate" data-testid="wizard-destination-preview">{form.destinationUrl}</p>
                )}
                <p className="text-[11px] text-slate-400 mt-1">Your flows are direct booking pages — perfect campaign landing spots. Every click is tagged so bookings trace back to this campaign.</p>
              </Field>
            </>
          )}

          {step === 3 && (
            <>
              {promoCodes.length > 0 && (
                <Field label="Include a discount code (optional)">
                  <div className="relative">
                    <Tag className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <select value={form.discountCode} data-testid="wizard-discount-select"
                      onChange={(e) => set({ discountCode: e.target.value })}
                      className={`${inputCls} pl-10`}>
                      <option value="">No discount</option>
                      {promoCodes.map((p) => (
                        <option key={p.code} value={p.code}>{p.code} — {p.label}{p.description ? ` · ${p.description.slice(0, 40)}` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">AI weaves the code into every ad, and emails show it as a copy-ready code chip.</p>
                </Field>
              )}

              <div className="flex items-center gap-2 flex-wrap mb-2">
                <button onClick={draft} disabled={drafting} data-testid="wizard-ai-draft-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#2563EB] text-white text-sm font-bold hover:bg-[#1D4ED8] disabled:opacity-60">
                  {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {variants.length ? 'Draft 5 more with AI' : 'Draft 5 ad variations with AI'}
                </button>
                <button onClick={addManual} data-testid="wizard-add-manual-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-slate-200 text-sm font-bold text-slate-600 hover:border-slate-300">
                  <Plus className="w-4 h-4" /> Write your own
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mb-4">
                Each contact gets a <span className="font-bold text-slate-500">different variation on every touch</span> — nobody sees the same ad twice until they've seen them all. Use <code className="bg-slate-100 px-1 rounded">{'{first_name}'}</code> and <code className="bg-slate-100 px-1 rounded">{'{link}'}</code> — personalized per contact.
              </p>

              {variants.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center" data-testid="wizard-variants-empty">
                  <Sparkles className="w-5 h-5 text-[#2563EB] mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Your ad hopper is empty — draft 5 variations with AI or write your own.</p>
                </div>
              ) : (
                <div className="space-y-3 mb-4" data-testid="wizard-variants-list">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                    Ad hopper · {kept.length} of {variants.length} kept
                  </div>
                  {variants.map((v, i) => (
                    <VariantCard key={v.key} v={v} i={i} channels={form.channels}
                      onPatch={patchVariant} onToggleKept={toggleKept} onToggleEdit={toggleEdit} />
                  ))}
                </div>
              )}

              {form.channels.includes('email') && imageOptions.length > 0 && (
                <Field label="Hero photo (top of every email)">
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid="wizard-image-picker">
                    <button onClick={() => set({ heroImageUrl: '' })} data-testid="wizard-image-auto"
                      className={`relative aspect-[4/3] rounded-lg border-2 flex flex-col items-center justify-center gap-1 text-[10px] font-bold transition-colors ${!form.heroImageUrl ? 'border-[#2563EB] bg-[#EBF2FF] text-[#2563EB]' : 'border-slate-200 text-slate-400 hover:border-slate-300'}`}>
                      <Sparkles className="w-4 h-4" /> Auto
                    </button>
                    {imageOptions.map((img) => (
                      <button key={img.url} onClick={() => set({ heroImageUrl: img.url })} title={img.service}
                        data-testid={`wizard-image-option`}
                        className={`relative aspect-[4/3] rounded-lg overflow-hidden border-2 transition-colors ${form.heroImageUrl === img.url ? 'border-[#2563EB]' : 'border-transparent hover:border-slate-300'}`}>
                        <img src={img.url} alt={img.service} className="w-full h-full object-cover" loading="lazy" />
                        {form.heroImageUrl === img.url && (
                          <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#2563EB] text-white flex items-center justify-center"><Check className="w-2.5 h-2.5" /></span>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">Photos come from your services. "Auto" lets us pick the best match for this campaign.</p>
                </Field>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <Field label="Campaign name">
                <input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="wizard-name-input"
                  placeholder={`${GOALS.find((g) => g.id === form.goal)?.title} · ${segName}`} className={inputCls} />
              </Field>
              <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 text-sm space-y-2" data-testid="wizard-review-summary">
                {[
                  ['Goal', GOALS.find((g) => g.id === form.goal)?.title],
                  ['Audience', `${segName} (opted-in only)`],
                  ['Channels', form.channels.map((c) => CHANNEL_META[c].label).join(' + ')],
                  ['Ad variations', `${kept.length} version${kept.length === 1 ? '' : 's'} rotating per contact`],
                  ['Discount', form.discountCode || '—'],
                  ['Daily budget', `${formatMoney(form.dailyBudgetCents / 100)} · ≈ ${reachText}`],
                  ['Link goes to', form.destinationUrl || 'Your website'],
                  ['Frequency cap', `Max 1 message per contact every ${form.frequencyDays} days`],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <span className="text-slate-400 font-bold text-xs uppercase tracking-wider pt-0.5">{k}</span>
                    <span className="font-semibold text-slate-800 text-right break-all">{v}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-3">Messages drip daily during local business hours (10am–7pm) and stop automatically when your credits or audience run out. Every message includes an opt-out.</p>
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
          <button onClick={() => (step === 0 ? onClose() : setStep(step - 1))} data-testid="wizard-back-btn"
            className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-slate-700">
            <ChevronLeft className="w-4 h-4" /> {step === 0 ? 'Cancel' : 'Back'}
          </button>
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="wizard-next-btn"
              className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[#2563EB] text-white text-sm font-bold hover:bg-[#1D4ED8] disabled:opacity-40">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => save(false)} disabled={saving} data-testid="wizard-save-draft-btn"
                className="px-4 py-2.5 rounded-full border border-slate-200 text-sm font-bold text-slate-600 hover:border-slate-300 disabled:opacity-50">
                Save draft
              </button>
              <button onClick={() => save(true)} disabled={saving} data-testid="wizard-launch-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2563EB] text-white text-sm font-bold hover:bg-[#1D4ED8] disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Launch
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
